import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import agreementsAPI from '../../services/agreementsAPI';
import Auth from '../../services/auth';
import http from '../../services/http';
import { switchToAccount } from '../../services/accountSwitcher';
import { btnClass } from '../../common/buttonClasses';
import { getImpersonationOrigin, restoreImpersonationOrigin } from '../../utils/impersonation';
import { clearUserSession } from '../../utils/sessionUtils';
import styles from './AgreementGate.module.css';
import TermsUpdateNotice from './TermsUpdateNotice';

type StoredUser = {
    isGodModeUser?: boolean;
    currentEntityId?: string;
    entityId?: string;
    platformContext?: string;
};

function readStoredUser(): StoredUser | null {
    try {
        return JSON.parse(localStorage.getItem('user') || '{}')?.user ?? null;
    } catch {
        return null;
    }
}

/**
 * The SANDBOX line and the upcoming-Terms strip, at the top of the page area (rendered by
 * SidebarLayout, under the fixed header). Same audience as the gate and its cached status call;
 * the holding and paused screens draw their own SANDBOX line.
 */
export const AgreementPageNotices: React.FC = () => {
    const user = useMemo(readStoredUser, []);
    const entityId = user?.currentEntityId || user?.entityId || '';
    const exempt = !user || !entityId || Boolean(user.isGodModeUser) || user.platformContext === 'aria_chat';
    const status = useQuery({
        queryKey: ['agreement-status', entityId],
        queryFn: agreementsAPI.getAgreementStatus,
        enabled: !exempt,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
    if (exempt || !status.data) return null;
    if (getImpersonationOrigin() && !status.data.isSandbox) return null;
    return (
        <>
            {status.data.isSandbox && (
                <div className={`${styles.sandboxStripe} ${styles.sandboxStripeInPage}`} role="note">
                    SANDBOX: test account. Nothing here is real.
                </div>
            )}
            <TermsUpdateNotice status={status.data} entityId={entityId} part="strip" />
        </>
    );
};

/** Ways off the holding and paused screens: another org, back to the impersonating account, or log out. */
const GateExits: React.FC<{ otherOrgs: Array<{ entityId: string; name: string }> }> = ({ otherOrgs }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const origin = getImpersonationOrigin();

    const switchTo = async (entityId: string) => {
        setBusy(true);
        setError(null);
        try {
            await switchToAccount(entityId);
        } catch {
            setError(t('header.switchAccountError', 'Failed to switch account. Please try again.'));
            setBusy(false);
        }
    };
    const switchBack = () => {
        const restored = restoreImpersonationOrigin();
        if (!restored) return;
        http.setAuthToken(restored.token);
        window.location.assign('/entities');
    };
    const logOut = async () => {
        setBusy(true);
        try {
            await Auth.logout();
        } catch {
            // Clear the local session even if the server call fails.
        }
        clearUserSession();
        window.location.href = '/login';
    };

    if (origin) {
        return (
            <div className={styles.actions}>
                <button type="button" className={btnClass({ variant: 'secondary' })} onClick={switchBack}>
                    {t('header.backToAccount', "Back to {{name}}'s account", { name: origin.user?.firstName })}
                </button>
            </div>
        );
    }
    return (
        <>
            {otherOrgs.length > 0 && (
                <>
                    <p className={styles.muted}>{t('agreementGate.otherOrgs', 'Open another organization:')}</p>
                    <div className={styles.actions}>
                        {otherOrgs.map((org) => (
                            <button
                                key={org.entityId}
                                type="button"
                                className={btnClass({ variant: 'secondary' })}
                                disabled={busy}
                                onClick={() => switchTo(org.entityId)}
                            >
                                {org.name}
                            </button>
                        ))}
                    </div>
                </>
            )}
            <div className={styles.actions}>
                <button type="button" className={btnClass({ variant: 'secondary' })} disabled={busy} onClick={logOut}>
                    {t('header.logOut', 'Log Out')}
                </button>
            </div>
            {error && <p className="form-error-message">{error}</p>}
        </>
    );
};

/**
 * Holds portal users of an org that hasn't accepted its Order Form yet, or whose access staff
 * paused for non-payment. God Mode, impersonation and Ask Aria users are never held, and any
 * error fails open: this gate must never lock out a paying customer because of a glitch.
 */
const AgreementGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const user = useMemo(readStoredUser, []);
    const entityId = user?.currentEntityId || user?.entityId || '';
    const exempt = !user || !entityId || Boolean(user.isGodModeUser) || user.platformContext === 'aria_chat';
    // "Log in as" normally skips the gate, except into a QA sandbox, where seeing it is the point.
    const impersonating = Boolean(getImpersonationOrigin());

    const status = useQuery({
        queryKey: ['agreement-status', entityId],
        queryFn: agreementsAPI.getAgreementStatus,
        enabled: !exempt,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
    const suspended = Boolean(status.data?.suspended);
    const standing = useQuery({
        queryKey: ['billing-standing', entityId],
        queryFn: agreementsAPI.getBillingStanding,
        enabled: !exempt && suspended,
        retry: false,
    });

    if (exempt || status.isError || !status.data) return <>{children}</>;
    if (impersonating && !status.data.isSandbox) return <>{children}</>;
    const sandboxStripe = status.data.isSandbox ? (
        <div className={styles.sandboxStripe} role="note">
            SANDBOX: test account. Nothing here is real.
        </div>
    ) : null;

    if (status.data.pendingAcceptance) {
        return (
            <main className={styles.page}>
                {sandboxStripe}
                <div className={styles.card}>
                    <h1 className={styles.title}>
                        {t('agreementGate.pendingTitle', 'Your organization is finishing setup')}
                    </h1>
                    <p>
                        {t(
                            'agreementGate.pendingBody',
                            "Your account owner needs to review and accept the Order Form we emailed them. As soon as it's accepted, you'll have full access."
                        )}
                    </p>
                    <p className={styles.muted}>
                        {t(
                            'agreementGate.pendingHelp',
                            "Can't find the email? Contact your Aptive Index advisor and we'll send it again."
                        )}
                    </p>
                    <GateExits otherOrgs={status.data.otherOrgs ?? []} />
                </div>
            </main>
        );
    }

    if (suspended) {
        const invoices = standing.data?.openInvoices ?? [];
        // Reason and pay links go to org admins only; everyone else is told to contact them.
        const member = Boolean(standing.data && !standing.data.isOrgAdmin);
        return (
            <main className={styles.page}>
                {sandboxStripe}
                <div className={styles.card}>
                    <h1 className={styles.title}>
                        {t('agreementGate.suspendedTitle', "Your organization's access is paused")}
                    </h1>
                    <p>
                        {member
                            ? t(
                                  'agreementGate.suspendedBodyMember',
                                  "Access is currently paused. Please contact your organization's administrator for assistance."
                              )
                            : t(
                                  'agreementGate.suspendedBody',
                                  'Access is paused because of an overdue invoice. Paying it restores access.'
                              )}
                    </p>
                    {standing.data?.suspendedReason && (
                        <p className={styles.muted}>
                            {t('agreementGate.reason', 'Reason: {{reason}}', { reason: standing.data.suspendedReason })}
                        </p>
                    )}
                    {invoices.length > 0 && (
                        <div className={styles.actions}>
                            {invoices.map((inv) => (
                                <a key={inv.invoiceId} className={btnClass({ variant: 'primary' })} href={inv.payUrl}>
                                    {t('agreementGate.viewInvoice', 'View invoice {{number}}', { number: inv.invoiceNumber })}
                                </a>
                            ))}
                        </div>
                    )}
                    {!member && (
                        <p className={styles.muted}>
                            {t('agreementGate.contact', 'Questions? Contact your Aptive Index advisor.')}
                        </p>
                    )}
                    <GateExits otherOrgs={status.data.otherOrgs ?? []} />
                </div>
            </main>
        );
    }

    return (
        <>
            <TermsUpdateNotice status={status.data} entityId={entityId} part="dialog" />
            {children}
        </>
    );
};

export default AgreementGate;
